import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchTranscriptForEpisode, fetchDiarizedTranscriptForEpisode } from '@services/index';
import { INSIGHTS_QUERY_KEY } from '@insights_module/constants/queryKeys';
import { ITranscript } from '@insights_module/types/Episode';
import { useInsightsContext } from '@insights_module/context/InsightsContext';

export const useTranscript = (id?: string) => {
  const { selectedEpisode } = useInsightsContext();
  const episodeId = useMemo(() => id || selectedEpisode, [id, selectedEpisode]);
  const [diarizedTranscriptError, setDiarizedTranscriptError] = useState<Error | null>(null);
  const [transcriptError, setTranscriptError] = useState<Error | null>(null);

  const { data: diarizedTranscript, isLoading: isDiarizedTranscriptLoading } = useQuery({
    queryKey: [INSIGHTS_QUERY_KEY.diarized_transcript, { episodeId }, transcriptError],
    queryFn: () => fetchDiarizedTranscriptForEpisode(episodeId, { language: 'en' }),
    enabled: !!episodeId && !diarizedTranscriptError && !transcriptError,
    staleTime: 60 * 60 * 1000, // 60 minutes
    retry: false,
    onError(err) {
      setDiarizedTranscriptError(err as Error);
    },
  });

  const { data: transcript, isLoading: isTranscriptLoading } = useQuery({
    queryKey: [INSIGHTS_QUERY_KEY.transcript, { episodeId }, diarizedTranscriptError],
    queryFn: () => fetchTranscriptForEpisode(episodeId),
    enabled: !!episodeId && !!diarizedTranscriptError,
    staleTime: 5 * 60 * 1000, // 5 minute
    onError(err) {
      setTranscriptError(err as Error);
    },
  });

  const _transcript: ITranscript[] = useMemo(() => {
    if (diarizedTranscript) {
      return diarizedTranscript?.data?.transcript?.results?.items || [];
    }

    if (transcript) {
      return transcript?.data?.transcript?.results?.items || [];
    }

    return [];
  }, [transcript, diarizedTranscript, episodeId]);

  const getPartialTranscript = useCallback(
    (startTime: number, endTime: number) => {
      if (!_transcript) return [] as ITranscript[];

      const start = _transcript?.findIndex((item) => Number(item.start_time) >= startTime);
      const end = _transcript?.findIndex((item) => Number(item.end_time) >= endTime);

      if (start === -1 || end === -1 || start === _transcript.length - 1)
        return [] as ITranscript[];

      // we have two cases: when punctuation has no time stamp or has time stamp. In both cases we want to include the punctuation after the word, so we check if last item is punctuation and already included or not
      const endIndex = _transcript[end].type === 'punctuation' ? end : end + 1;

      // +1 because slice is exclusive
      return _transcript?.slice(start, endIndex + 1);
    },
    [_transcript]
  );

  const transcriptDuration = useMemo(
    () =>
      _transcript
        ?.slice(-10)
        .reverse()
        .find((item) => item.type === 'pronunciation')?.end_time,
    [_transcript]
  );

  useEffect(() => {
    setDiarizedTranscriptError(null);
    setTranscriptError(null);
  }, [episodeId]);
  const isLoading = useMemo(() => {
    if (!episodeId || transcriptError) return false;

    if (diarizedTranscriptError) return isTranscriptLoading;

    return isDiarizedTranscriptLoading;
  }, [
    isDiarizedTranscriptLoading,
    diarizedTranscriptError,
    isTranscriptLoading,
    transcriptError,
    episodeId,
  ]);

  return {
    transcript: _transcript,
    isLoading,
    getPartialTranscript,
    transcriptDuration,
    isError: diarizedTranscriptError && transcriptError,
  };
};
